/**
 * RecentLoginsWidget — affiche les 3-5 dernières connexions du compte
 * avec IP / pays / navigateur / appareil / horodatage.
 *
 * Style « banque en ligne » : composant compact, sobre, rassurant.
 * Visible sur ExpertDashboard + UserDashboard.
 */
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Monitor, Smartphone, MapPin, ShieldCheck, AlertCircle, Clock } from "lucide-react";

function formatRelativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function maskIp(ip) {
  if (!ip || ip === "-") return "—";
  // IPv4 : on masque le dernier octet
  const m4 = ip.match(/^(\d+\.\d+\.\d+)\.\d+$/);
  if (m4) return `${m4[1]}.xxx`;
  // IPv6 : on garde les 4 premiers blocs
  const m6 = ip.match(/^([0-9a-f:]+:[0-9a-f]+):/i);
  if (m6) return `${m6[1]}:…`;
  return ip;
}

export default function RecentLoginsWidget({ limit = 5, className = "" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.get(`/auth/me/recent-logins?limit=${limit}`)
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || "Erreur de chargement"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  return (
    <Card className="p-5" data-testid="recent-logins-widget">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-copper mb-1 inline-flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Audit de connexion
          </p>
          <h3 className="font-serif italic text-xl text-night">Dernières connexions à votre compte</h3>
          <p className="text-[11px] text-night/55 mt-0.5">
            Une activité ne vous semble pas familière ? Contactez immédiatement notre support.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-night/55 italic">Chargement…</p>
      ) : error ? (
        <p className="text-sm text-bordeaux flex items-center gap-2" data-testid="recent-logins-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-night/55 italic">Aucune connexion enregistrée pour le moment.</p>
      ) : (
        <ul className="divide-y divide-sage/60" data-testid="recent-logins-list">
          {items.map((it, i) => {
            const Icon = it.ua_device === "Mobile" ? Smartphone : Monitor;
            const isFailed = it.action === "auth.login.failed";
            return (
              <li key={i} className="py-3 flex items-start gap-3" data-testid={`recent-login-${i}`}>
                <span className={`mt-0.5 p-1.5 rounded-sm shrink-0 ${isFailed ? "bg-bordeaux/10 text-bordeaux" : "bg-petrol/10 text-petrol"}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-semibold ${isFailed ? "text-bordeaux" : "text-night"}`}>
                      {isFailed ? "Tentative échouée" : "Connexion réussie"}
                    </span>
                    {it.auth_level === "2fa" && !isFailed && (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-petrol px-1.5 py-0.5 rounded-sm border border-petrol/30 bg-petrol/5">
                        <ShieldCheck className="inline w-3 h-3 mr-0.5" />2FA
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-night/65 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <Clock className="w-3 h-3" />{formatRelativeTime(it.created_at)}
                    <span className="mx-1 text-night/30">·</span>
                    <MapPin className="w-3 h-3" />
                    <span>{maskIp(it.ip)}{it.country && it.country !== "-" ? ` · ${it.country}` : ""}</span>
                    <span className="mx-1 text-night/30">·</span>
                    <span>{it.ua_browser || "Navigateur inconnu"} sur {it.ua_os || "OS inconnu"}</span>
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
